import React, { useMemo, useState } from 'react';

import {
  blanketInsuranceApis,
  dashboardBlanketInsuranceApis,
} from '@apis/next/blanketInsuranceApis';
import addSquareIcon from '@assets/img/icons/add-square.svg';
import refreshIcon from '@assets/img/icons/refresh.svg';
import subtractSquareIcon from '@assets/img/icons/subtract-square.svg';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogContent,
  ActionableDialogContentDivider,
  ActionableDialogTitle,
} from '@components/Dialog';
import { FileDisplay } from '@components/FileDisplay';
import { IconButton } from '@components/IconButton';
import { GridForm } from '@components/NextForms/GridForm';
import { QueryContainer } from '@components/QueryContainer';
import { instructionStrings } from '@data/instruction';
import { useIsAdmin } from '@hooks/useIsAdmin';
import {
  BlanketInsuranceSearchQuery,
  type BlanketInsurance,
  BlanketInsuranceBase,
  BlanketInsuranceHelpers,
} from '@models/next/BlanketInsurance';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { formatDate } from '@utils/types';
import clsx from 'clsx';

import { url } from '../../../../.split-pages/meta';

const strings = createStrings({
  ja: {
    chooseBlanketInsurancePrompt: `${instructionStrings.insuranceTags.BLANKET}を選択してください`,
    askCustomerToEditBlanketInsurance: (
      <>
        *CSはお客さんの{instructionStrings.insuranceTags.BLANKET}
        を変更または追加することはできません。
        <br />
        お客さんの{instructionStrings.insuranceTags.BLANKET}
        が不足している場合は、お客様に連絡して、お客様自身の
        {instructionStrings.insuranceTags.BLANKET}リストを改善するよう依頼してください。
      </>
    ),
    expired: '期限切れ',
    addBlanketInsurance: `${instructionStrings.insuranceTags.BLANKET}を追加`,
    editOrDeleteBlanketInsurance: `${instructionStrings.insuranceTags.BLANKET}を編集または削除`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    padding: '1rem 1rem',
  },
  contentRoot: {
    paddingTop: '1rem',
    paddingBottom: '0.5rem',
  },
  actionsContainer: {
    ...common.flexInline,
    flexWrap: 'wrap',
    columnGap: '1.5rem',
    [theme.breakpoints.down('sm')]: {
      columnGap: '8rem',
      rowGap: '0.5rem',
    },
    [theme.breakpoints.down('xs')]: {
      ...common.flexColumn,
    },
  },
  linksContainer: {
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.justifyBetween,
  },
  blanketInsuranceList: {
    height: 360,
    overflow: 'auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(4,minmax(0,1fr))',
    rowGap: 24,
    columnGap: 15,
    padding: '13px 23px',
  },
  blanketInsuranceItemContainer: {
    ...common.flexColumn,
    ...common.baseButton,
    position: 'relative',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 2,
    aspectRatio: '1 / 1',
    '&$selected': {
      borderColor: theme.palette.primary.main,
    },
    '&$disabled': {
      cursor: 'not-allowed',
    },
  },
  blanketInsuranceItemFileDisplay: {
    flex: 1,
    minHeight: 0,
  },
  blanketInsuranceItemLabel: {
    ...common.backgroundGray50,
    ...common.textAlignCenter,
    ...common.middleText,
    ...common.preEllipsis,
    padding: '2px 4px',
    '$disabled &': {
      ...common.textGray700Important,
    },
  },
  blanketInsuranceItemPeriod: {
    ...common.backgroundGray50,
    ...common.smallerText,
    ...common.textAlignCenter,
    ...common.preEllipsis,
    borderTop: `1px solid ${theme.palette.common.white}`,
    padding: '2px 4px',
    '$selected &': {
      ...common.textWhite,
      ...common.backgroundPrimary,
    },
    '$disabled &': {
      ...common.textGray700Important,
      ...common.backgroundGray50,
    },
  },
  blanketInsuranceItemExpiredTag: {
    ...common.backgroundGray500,
    ...common.textWhite,
    ...common.smallerText,
    position: 'absolute',
    top: 0,
    left: 0,
    padding: '2px 6px',
  },
  selected: {},
  disabled: {},
}));

const BlanketInsuranceSearch: React.FC<{
  queries: BlanketInsuranceSearchQuery;
  setQueries: (q: BlanketInsuranceSearchQuery) => void;
}> = ({ queries, setQueries }) => {
  const form = useForm(BlanketInsuranceSearchQuery, {
    initialOutput: queries,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  const isAdmin = useIsAdmin();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const queryClient = useQueryClient();

  return (
    <form {...form.bindForm({ onSubmit: (v) => setQueries({ ...v, page: 1 }) })}>
      <GridForm columns={3} fields={[fields.company, fields.policy_no, fields.valid_date]} />

      <div className={classes.linksContainer}>
        {!isAdmin && (
          <div className={classes.actionsContainer}>
            <IconButton
              icon={<img src={addSquareIcon} alt="Add Icon" />}
              text={strings.addBlanketInsurance}
              onClick={() =>
                window.open(url('/utilities/BlanketInsurance/AddBlanketInsurance'), '_blank')
              }
            />
            <IconButton
              icon={<img src={subtractSquareIcon} alt="Minus Icon" />}
              text={strings.editOrDeleteBlanketInsurance}
              onClick={() =>
                window.open(url('/utilities/BlanketInsurance/BlanketInsurances'), 'blank')
              }
            />
            <IconButton
              icon={<img src={refreshIcon} alt="Refresh Icon" />}
              text={formStrings.update}
              onClick={() => {
                queryClient.invalidateQueries(
                  isAdmin
                    ? dashboardBlanketInsuranceApis.getKeyByPath(
                        'get',
                        '/dashboard/blanket-insurance',
                      )
                    : blanketInsuranceApis.getKeyByPath('get', '/blanket-insurance'),
                );
              }}
            />
          </div>
        )}
        {isAdmin && (
          <div className={clsx(classes.textError, classes.smallerText)}>
            {strings.askCustomerToEditBlanketInsurance}
          </div>
        )}

        <Button
          text={formStrings.search}
          style={{ width: 88 }}
          type="submit"
          color="primary"
          size="small"
        />
      </div>
    </form>
  );
};

const BlanketInsuranceItem: React.FC<{
  item: BlanketInsurance;
  selected: boolean;
  onSelect: () => void;
}> = ({ item, selected, onSelect }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const disabled = useMemo(() => BlanketInsuranceHelpers.isExpired(item), [item]);

  return (
    <div
      className={clsx(
        classes.blanketInsuranceItemContainer,
        selected && classes.selected,
        disabled && classes.disabled,
      )}
      onClick={() => !disabled && onSelect()}
    >
      <FileDisplay
        file={item.blanket_insurance_files.at(0)}
        className={classes.blanketInsuranceItemFileDisplay}
        withPdfFileName
      />
      <div className={classes.blanketInsuranceItemLabel}>
        {item.company} {item.policy_no}
      </div>
      <div className={classes.blanketInsuranceItemPeriod}>
        {formatDate(item.start)} - {formatDate(item.end)}
      </div>

      {disabled && <div className={classes.blanketInsuranceItemExpiredTag}>{strings.expired}</div>}
    </div>
  );
};

export interface BlanketInsuranceFilterProps {
  open: boolean;
  value?: BlanketInsuranceBase;
  onClose: (value?: BlanketInsuranceBase) => void;
  userId: number;
}

export const BlanketInsuranceFilter: React.FC<BlanketInsuranceFilterProps> = (props) => {
  const { open, value, onClose, userId } = props;

  const isAdmin = useIsAdmin();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [queries, setQueries] = useState<BlanketInsuranceSearchQuery>({});

  const [selected, setSelected] = useState<BlanketInsuranceBase | undefined>(value);

  const blanketInsuranceQuery = isAdmin
    ? dashboardBlanketInsuranceApis.useGet('/dashboard/blanket-insurance', {
        queries,
      })
    : blanketInsuranceApis.useGet('/blanket-insurance', {
        queries: {
          ...queries,
          user_id: userId,
        },
      });

  return (
    <ActionableDialog
      open={open}
      onClose={() => onClose()}
      size="lg"
      muiDialogProps={{ disablePortal: false }}
    >
      <ActionableDialogTitle>{strings.chooseBlanketInsurancePrompt}</ActionableDialogTitle>

      <ActionableDialogContent classes={{ root: classes.contentRoot }}>
        <BlanketInsuranceSearch queries={queries} setQueries={setQueries} />
      </ActionableDialogContent>

      <ActionableDialogContentDivider />

      <QueryContainer style={{ height: 360 }} queries={[blanketInsuranceQuery]}>
        <div className={classes.blanketInsuranceList}>
          {blanketInsuranceQuery.data?.map((item) => (
            <BlanketInsuranceItem
              key={item.id}
              item={item}
              selected={selected ? BlanketInsuranceHelpers.areEqualValue(item, selected) : false}
              onSelect={() => setSelected(item)}
            />
          ))}
        </div>
      </QueryContainer>

      <ActionableDialogActions>
        <Button
          type="button"
          text={formStrings.ok}
          color="primary"
          size="small"
          onClick={() => onClose(selected)}
          style={{ width: 88 }}
        />
      </ActionableDialogActions>
    </ActionableDialog>
  );
};
